import { useCallback, useEffect, useState } from "react";
import { collection, doc, onSnapshot, query } from "firebase/firestore";
import {
  COLLECTIONS,
  SUBCOLLECTIONS,
} from "@/integrations/firebase/collections";
import { logError } from "@/shared/services/ErrorReporting";
import { db } from "@/integrations/firebase/firestore";
import type { House } from "./types";

export interface UseHousesResult {
  houses: House[];
  /** A house's display name from its id, falling back to the id — the same
   *  hop `useBusinessUnits` makes for units, for the same reason: membership
   *  and scope are stored as ids and read by people. */
  houseName: (id: string | null | undefined) => string;
  loading: boolean;
  error: Error | null;
}

/**
 * The houses inside one business unit (ADR 0025) — `business_units/{buId}/houses`.
 *
 * Readable by the unit's members, any BU admin and the group's roles, so the
 * same hook serves the Houses page, the Users page's house column, and a
 * member's own "you are in Rowohlt". A tenant with no houses yields an empty
 * list, which is the state every unit but HBV is in.
 */
export function useHouses(businessUnitId: string | null | undefined): UseHousesResult {
  const [houses, setHouses] = useState<House[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!businessUnitId) {
      setHouses([]);
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, COLLECTIONS.BUSINESS_UNITS, businessUnitId, SUBCOLLECTIONS.HOUSES),
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setHouses(
          snapshot.docs
            .map((d) => ({
              ...(d.data() as Omit<House, "id" | "businessUnitId">),
              id: d.id,
              businessUnitId,
            }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        );
        setLoading(false);
      },
      (err) => {
        logError(err);
        setError(err);
        setLoading(false);
      },
    );
    return () => unsubscribe();
  }, [businessUnitId]);

  const houseName = useCallback(
    (id: string | null | undefined) =>
      id ? (houses.find((house) => house.id === id)?.name ?? id) : "",
    [houses],
  );

  return { houses, houseName, loading, error };
}

export interface UseHouseResult {
  house: House | null;
  loading: boolean;
  error: Error | null;
}

/** One house, live — for a house admin's own settings and the editor's
 *  "this is your house" heading. `null` while there is no house to read, which
 *  is the ordinary state of every member of the unit itself. */
export function useHouse(
  businessUnitId: string | null | undefined,
  houseId: string | null | undefined,
): UseHouseResult {
  const [house, setHouse] = useState<House | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!businessUnitId || !houseId) {
      setHouse(null);
      setLoading(false);
      return;
    }
    const unsubscribe = onSnapshot(
      doc(db, COLLECTIONS.BUSINESS_UNITS, businessUnitId, SUBCOLLECTIONS.HOUSES, houseId),
      (snap) => {
        setHouse(
          snap.exists()
            ? {
                ...(snap.data() as Omit<House, "id" | "businessUnitId">),
                id: snap.id,
                businessUnitId,
              }
            : null,
        );
        setLoading(false);
      },
      (err) => {
        // A refused read answers who may see the house; it is not a fault,
        // and callers treat it as no house.
        if (err.code !== "permission-denied") logError(err);
        setError(err);
        setLoading(false);
      },
    );
    return () => unsubscribe();
  }, [businessUnitId, houseId]);

  return { house, loading, error };
}
